import { AlertTriangle, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { cx, TONE_CLASSES, type Tone } from "../styles";
import { toneFor } from "./statusTone";

/**
 * A state, as a pill.
 *
 * Two ways to use it, and the first is the one to reach for. Give it a `domain`
 * and a `status` and it asks {@link toneFor} what colour that is — which is
 * what keeps `on_hold` the same amber on the orders table, the fulfilment
 * board and the store portal. Give it a `tone` directly only when the thing
 * being labelled is not a domain status at all.
 *
 * **The label is always rendered.** The colour is emphasis; the words are the
 * information. That is what makes the chip readable to somebody who cannot
 * distinguish amber from green, and it is why an unknown status renders grey
 * with its own name rather than failing.
 */

export type StatusChipSize = "sm" | "md";

interface CommonProps {
	/** What the chip says. Defaults to the status, humanised. */
	children?: ReactNode;
	size?: StatusChipSize;
	/** An icon before the label, for a state worth pointing at. */
	icon?: LucideIcon;
	className?: string;
}

interface FromStatus extends CommonProps {
	/** The module the status belongs to: `order`, `product`, `return`. */
	domain: string;
	status: string;
	tone?: never;
}

interface FromTone extends CommonProps {
	tone: Tone;
	domain?: never;
	status?: never;
}

export type StatusChipProps = FromStatus | FromTone;

const SIZES: Record<StatusChipSize, string> = {
	sm: "h-5 px-2 text-[11px] gap-1",
	md: "h-[22px] px-2.5 text-[11.5px] gap-1.5",
};

export function StatusChip(props: StatusChipProps) {
	const { children, size = "md", icon, className } = props;

	const resolved =
		props.tone !== undefined
			? { tone: props.tone, alert: false }
			: toneFor(props.domain, props.status);

	// `conflict` and `variance` carry an alert triangle as well as a colour,
	// because "the numbers disagree" has to be distinguishable from "waiting"
	// at a glance and both are amber.
	const glyph = icon ?? (resolved.alert === true ? AlertTriangle : undefined);

	return (
		<span
			className={cx(
				"inline-flex items-center whitespace-nowrap rounded-full border font-bold",
				SIZES[size],
				TONE_CLASSES[resolved.tone],
				className,
			)}
		>
			{glyph && <Icon as={glyph} size={size === "sm" ? 11 : 12} />}
			{children ?? (props.status !== undefined ? humanise(props.status) : null)}
		</span>
	);
}

/**
 * `out_for_delivery` → `Out for delivery`.
 *
 * A fallback, not the intended path. A real screen passes a translated label as
 * the child, because these words appear in Arabic too and a humanised
 * identifier is English by construction. This exists so a chip is never blank
 * while a translation is being written, and so the demo page can render the
 * whole vocabulary without a bundle entry per status.
 */
export function humanise(status: string): string {
	const words = status.trim().toLowerCase().replaceAll(/[_-]+/g, " ");
	return words.charAt(0).toUpperCase() + words.slice(1);
}
